import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { format, differenceInCalendarDays, addDays } from "date-fns";
import { pt } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { CheckCircle2, Loader2, ArrowRight, CalendarDays, CreditCard, Wallet } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iso = (d) => format(d, "yyyy-MM-dd");
const parseISO = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export default function BookingWidget() {
  const [unavailable, setUnavailable] = useState([]);
  const [range, setRange] = useState(undefined);
  const [form, setForm] = useState({ guest_name: "", guest_email: "", phone: "", guests: "13" });
  const [paymentMethod, setPaymentMethod] = useState("pay_now");
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const quoteTimer = useRef(null);

  const loadAvailability = async () => {
    try {
      const { data } = await axios.get(`${API}/availability`);
      setUnavailable(data.unavailable || []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    loadAvailability();
  }, []);

  const guestsCount = useMemo(() => {
    const n = parseInt(form.guests, 10);
    return Number.isFinite(n) ? Math.max(1, Math.min(n, 13)) : 13;
  }, [form.guests]);

  // Orçamento em tempo real (debounced)
  useEffect(() => {
    if (!range?.from || !range?.to) {
      setQuote(null);
      return;
    }
    setQuoteLoading(true);
    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/bookings/quote`, {
          check_in: iso(range.from),
          check_out: iso(range.to),
          guests_count: guestsCount,
        });
        setQuote(data);
      } catch {
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 300);
    return () => clearTimeout(quoteTimer.current);
  }, [range, guestsCount]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Disabled matchers: past days + every unavailable range (inclusive).
  const disabledMatchers = useMemo(() => {
    const matchers = [{ before: today }];
    unavailable.forEach((r) => {
      matchers.push({ from: parseISO(r.check_in), to: parseISO(r.check_out) });
    });
    return matchers;
  }, [unavailable, today]);

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) {
      toast.error("Selecione as datas de entrada e saída no calendário.");
      return;
    }
    if (!form.guest_name.trim() || !form.guest_email.trim()) {
      toast.error("Preencha o seu nome e email.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        guests_count: guestsCount,
        payment_method: paymentMethod,
        check_in: iso(range.from),
        check_out: iso(range.to),
        message: `Reserva online · ${nights} noite(s)`,
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      const booking = data.booking || data;
      setDone({ ...booking, payment_instructions: data.payment_instructions || null });
      toast.success(data.payment_instructions
        ? "Datas reservadas! Siga as instruções de pagamento."
        : "Reserva confirmada! Enviámos um email de confirmação.");
      setRange(undefined);
      setForm({ guest_name: "", guest_email: "", phone: "", guests: "13" });
      loadAvailability();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível concluir a reserva.");
      loadAvailability();
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const instr = done.payment_instructions;
    return (
      <motion.div
        data-testid="booking-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-forest text-cream p-8 lg:p-12 rounded-sm"
      >
        <CheckCircle2 size={44} strokeWidth={1.25} className="text-cream/90" />
        {instr ? (
          <>
            <h3 className="font-display text-3xl lg:text-4xl mt-6">Datas reservadas. Falta só o pagamento.</h3>
            <p className="mt-4 text-cream/80 leading-relaxed max-w-md" data-testid="booking-pending-note">
              Obrigado, {done.guest_name}. As suas datas estão bloqueadas de{" "}
              <strong>{format(parseISO(done.check_in), "d 'de' MMMM", { locale: pt })}</strong> a{" "}
              <strong>{format(parseISO(done.check_out), "d 'de' MMMM yyyy", { locale: pt })}</strong>.
              Para garantir a estadia, pague <strong>{(done.price_due ?? done.price_total)?.toFixed(2)} €</strong> por um destes métodos:
            </p>
            <div className="mt-6 space-y-3 max-w-md" data-testid="booking-payment-instructions">
              {instr.mbway && (
                <div className="border border-cream/25 rounded-sm p-4">
                  <div className="text-[0.65rem] tracking-[0.2em] uppercase text-cream/60">MB Way</div>
                  <div className="text-lg font-medium mt-1">{instr.mbway}</div>
                </div>
              )}
              {instr.revolut && (
                <div className="border border-cream/25 rounded-sm p-4">
                  <div className="text-[0.65rem] tracking-[0.2em] uppercase text-cream/60">Revolut</div>
                  <div className="text-lg font-medium mt-1">{instr.revolut}</div>
                </div>
              )}
              {instr.iban && (
                <div className="border border-cream/25 rounded-sm p-4">
                  <div className="text-[0.65rem] tracking-[0.2em] uppercase text-cream/60">Transferência Bancária</div>
                  <div className="text-lg font-medium mt-1 break-all">{instr.iban}</div>
                  <div className="text-sm text-cream/70 mt-0.5">Titular: {instr.holder}</div>
                </div>
              )}
            </div>
            <p className="mt-6 text-cream/70 text-sm leading-relaxed max-w-md">
              A sua reserva será <strong className="text-cream">confirmada em menos de 24 horas</strong> após
              confirmarmos o pagamento. Enviámos estas instruções também para {done.guest_email}.
            </p>
          </>
        ) : (
          <>
            <h3 className="font-display text-3xl lg:text-4xl mt-6">Reserva confirmada.</h3>
            <p className="mt-4 text-cream/80 leading-relaxed max-w-md">
              Obrigado, {done.guest_name}. As suas datas estão garantidas de{" "}
              <strong>{format(parseISO(done.check_in), "d 'de' MMMM", { locale: pt })}</strong> a{" "}
              <strong>{format(parseISO(done.check_out), "d 'de' MMMM yyyy", { locale: pt })}</strong>.
              Enviámos um email de confirmação para {done.guest_email}.
            </p>
          </>
        )}
        <button
          data-testid="booking-new-btn"
          onClick={() => setDone(null)}
          className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full border border-cream/40 text-cream text-[0.78rem] tracking-[0.2em] uppercase hover:bg-cream/10 transition-colors"
        >
          Fazer outra reserva
        </button>
      </motion.div>
    );
  }

  return (
    <div data-testid="booking-widget" className="bg-white border border-border rounded-sm overflow-hidden">
      <div className="grid lg:grid-cols-2">
        {/* Calendar */}
        <div className="p-6 lg:p-8 border-b lg:border-b-0 lg:border-r border-border">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays size={16} strokeWidth={1.5} className="text-terracotta" />
            <p className="overline">Escolha as datas</p>
          </div>
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            disabled={disabledMatchers}
            excludeDisabled
            numberOfMonths={1}
            locale={pt}
            fromDate={today}
            data-testid="booking-calendar"
            className="p-0"
            classNames={{
              day_selected: "bg-forest text-cream hover:bg-forest hover:text-cream focus:bg-forest",
              day_range_middle: "aria-selected:bg-[#EFEBE0] aria-selected:text-forest",
              day_today: "text-terracotta font-semibold",
              day: "h-9 w-9 p-0 font-normal rounded-md hover:bg-[#EFEBE0] aria-selected:opacity-100",
              caption_label: "text-sm font-medium capitalize text-forest font-display text-lg",
              head_cell: "text-muted-foreground w-9 font-normal text-[0.7rem] uppercase tracking-wider",
            }}
          />
          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-forest inline-block" /> Selecionado</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-border inline-block" /> Indisponível</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} data-testid="booking-form" className="p-6 lg:p-8">
          <AnimatePresence mode="wait">
            {range?.from && range?.to ? (
              <motion.div
                key="summary"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-[#EFEBE0] rounded-sm flex items-center justify-between"
                data-testid="booking-summary"
              >
                <div>
                  <div className="text-forest font-medium capitalize">
                    {format(range.from, "d MMM", { locale: pt })} → {format(range.to, "d MMM", { locale: pt })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{nights} noite(s)</div>
                </div>
                <CalendarDays size={22} strokeWidth={1.25} className="text-terracotta" />
              </motion.div>
            ) : (
              <motion.p key="hint" className="mb-6 text-sm text-muted-foreground">
                Selecione a data de entrada e de saída no calendário para continuar.
              </motion.p>
            )}
          </AnimatePresence>

          <div className="space-y-5">
            <Field label="Nome *">
              <input data-testid="booking-name-input" name="guest_name" value={form.guest_name} onChange={onChange} required className="input-line" placeholder="O seu nome" />
            </Field>
            <Field label="Email *">
              <input data-testid="booking-email-input" type="email" name="guest_email" value={form.guest_email} onChange={onChange} required className="input-line" placeholder="voce@email.com" />
            </Field>
            <div className="grid grid-cols-2 gap-5">
              <Field label="Telefone">
                <input data-testid="booking-phone-input" name="phone" value={form.phone} onChange={onChange} className="input-line" placeholder="+351 ..." />
              </Field>
              <Field label="Nº de hóspedes">
                <input data-testid="booking-guests-input" type="number" min="1" max="13" name="guests" value={form.guests} onChange={onChange} className="input-line" placeholder="13" />
              </Field>
            </div>
          </div>

          {/* Orçamento */}
          <AnimatePresence>
            {(quote || quoteLoading) && range?.from && range?.to && (
              <motion.div
                key="quote"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-6 p-5 border border-border rounded-sm bg-white"
                data-testid="booking-quote"
              >
                {quoteLoading || !quote ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" /> A calcular o preço...</div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">{quote.nights} noite(s) · {quote.guests} hóspede(s)</span>
                    <div className="text-right">
                      <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Total</div>
                      <span className="font-display text-3xl text-forest" data-testid="booking-quote-total">{quote.total.toFixed(2)} €</span>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Método de pagamento */}
          {quote && !quoteLoading && (
            <div className="mt-5 space-y-3" data-testid="booking-payment-methods">
              <p className="text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground">Como quer pagar?</p>
              <button
                type="button"
                onClick={() => setPaymentMethod("pay_now")}
                data-testid="payment-paynow-option"
                className={`w-full text-left p-4 rounded-sm border transition-all ${paymentMethod === "pay_now" ? "border-forest bg-forest/5 ring-1 ring-forest" : "border-border hover:border-forest/50"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <CreditCard size={18} strokeWidth={1.5} className="text-terracotta shrink-0" />
                    <div>
                      <div className="font-medium text-forest text-sm">Pagar já — poupe 10%</div>
                      <div className="text-xs text-muted-foreground">MB Way, Revolut ou transferência · confirmamos em menos de 24h</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-xl text-terracotta">{quote.pay_now_total.toFixed(2)} €</div>
                    <div className="text-[0.68rem] text-forest/70 line-through">{quote.total.toFixed(2)} €</div>
                  </div>
                </div>
                <div className="mt-2 text-[0.68rem] tracking-wide uppercase text-terracotta font-medium">Poupa {quote.pay_now_discount.toFixed(2)} € (−10%)</div>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("pay_at_property")}
                data-testid="payment-property-option"
                className={`w-full text-left p-4 rounded-sm border transition-all ${paymentMethod === "pay_at_property" ? "border-forest bg-forest/5 ring-1 ring-forest" : "border-border hover:border-forest/50"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Wallet size={18} strokeWidth={1.5} className="text-forest shrink-0" />
                    <div>
                      <div className="font-medium text-forest text-sm">Pagar no alojamento</div>
                      <div className="text-xs text-muted-foreground">Confirmação imediata, sem desconto</div>
                    </div>
                  </div>
                  <div className="font-display text-xl text-forest shrink-0">{quote.total.toFixed(2)} €</div>
                </div>
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit-btn"
            className="mt-8 w-full group inline-flex items-center justify-center gap-3 h-14 rounded-full bg-forest text-cream font-medium text-[0.82rem] tracking-[0.2em] uppercase hover:bg-[#1f2f21] transition-all disabled:opacity-60"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> A reservar...</>
            ) : (
              <>{paymentMethod === "pay_now" ? "Reservar e Pagar" : "Confirmar Reserva"} <ArrowRight size={16} strokeWidth={1.75} className="group-hover:translate-x-1 transition-transform" /></>
            )}
          </button>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            {paymentMethod === "pay_now"
              ? "MB Way, Revolut ou transferência bancária. Reserva confirmada em menos de 24h após o pagamento."
              : "Sem pagamento online. Confirmação imediata por email."}
          </p>
        </form>
      </div>

      <style>{`
        .input-line { width:100%; background:transparent; border:none; border-bottom:1px solid hsl(var(--border)); padding:10px 0; font-family:'Manrope',sans-serif; font-size:0.95rem; color:hsl(var(--foreground)); transition:border-color .2s ease; outline:none; }
        .input-line::placeholder { color:hsl(var(--muted-foreground) / 0.6); }
        .input-line:focus { border-bottom-color:hsl(var(--primary)); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}
